import type {
  AdultModelApplicationRecord,
  AdultModelApplicationStatus,
  ChildModelApplicationStatus,
  ModelApplicationNotificationSettings,
} from '../types';
import { getSiteAuthRuntime, getStoredToken } from './bliss-app-auth';
import { requestPublicModelApplicationsApi } from './public-model-applications-api';

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') return fallback;

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
    data?: { message?: string };
  };

  if (typeof source.error === 'string' && source.error) return source.error;
  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }
  if (typeof source.message === 'string' && source.message) return source.message;
  if (source.data && typeof source.data.message === 'string' && source.data.message) return source.data.message;
  return fallback;
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function requestAdultModelApplicationsApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();
  const headers = new Headers(options.headers);
  const method = (options.method || 'GET').toUpperCase();
  const hasBody = options.body != null;

  if (!token) {
    throw new Error('You need to sign in before managing adult model applications.');
  }

  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    ...options,
    method,
    headers,
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The adult model application service could not be reached right now.'));
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

export async function getMyAdultModelApplication(input: {
  eventId: string;
  ticketId: string;
}): Promise<AdultModelApplicationRecord | null> {
  const search = new URLSearchParams({
    event_id: input.eventId,
    ticket_id: input.ticketId,
  });

  const result = await requestAdultModelApplicationsApi<{ application: AdultModelApplicationRecord | null }>(
    `/events/model-applications/me?${search.toString()}`,
  );

  return result.application ?? null;
}

export async function submitAdultModelApplication(input: {
  eventId: string;
  ticketId: string;
  applicationMessage?: string;
  availabilityDetails?: string;
}): Promise<AdultModelApplicationRecord> {
  const result = await requestAdultModelApplicationsApi<{ application: AdultModelApplicationRecord }>(
    '/events/model-applications',
    {
      method: 'POST',
      body: JSON.stringify({
        event_id: input.eventId,
        ticket_id: input.ticketId,
        application_message: input.applicationMessage?.trim() || '',
        availability_details: input.availabilityDetails?.trim() || '',
      }),
    },
  );

  return result.application;
}

export async function submitPublicAdultModelApplication(input: {
  eventId: string;
  ticketId: string;
  displayName: string;
  email: string;
  phone: string;
  birthday: string;
  city: string;
  state: string;
  height?: string;
  instagramHandle?: string;
  experienceSummary?: string;
  applicationMessage?: string;
  availabilityDetails?: string;
}): Promise<AdultModelApplicationRecord> {
  const result = await requestPublicModelApplicationsApi<{ application: AdultModelApplicationRecord }>(
    '/public/events/model-applications',
    {
      method: 'POST',
      body: JSON.stringify({
        event_id: input.eventId,
        ticket_id: input.ticketId,
        display_name: input.displayName.trim(),
        email: input.email.trim(),
        phone: input.phone.trim(),
        birthday: input.birthday.trim(),
        city: input.city.trim(),
        state: input.state.trim(),
        height: input.height?.trim() || '',
        instagram_handle: input.instagramHandle?.trim() || '',
        experience_summary: input.experienceSummary?.trim() || '',
        application_message: input.applicationMessage?.trim() || '',
        availability_details: input.availabilityDetails?.trim() || '',
      }),
    },
    'The adult model application form could not be submitted right now.',
  );

  return result.application;
}

export async function listAdultModelApplications(options: {
  eventId?: string;
  status?: AdultModelApplicationStatus | 'all';
  limit?: number;
} = {}): Promise<AdultModelApplicationRecord[]> {
  const search = new URLSearchParams();
  if (options.eventId) {
    search.set('event_id', options.eventId);
  }
  if (options.status && options.status !== 'all') {
    search.set('status', options.status);
  }
  if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
    search.set('limit', String(options.limit));
  }

  const result = await requestAdultModelApplicationsApi<{ applications: AdultModelApplicationRecord[] }>(
    `/events/model-applications${search.toString() ? `?${search.toString()}` : ''}`,
  );

  return Array.isArray(result.applications) ? result.applications : [];
}

export async function updateAdultModelApplicationStatus(
  applicationId: string,
  input: {
    status: AdultModelApplicationStatus;
    reviewNotes?: string;
  },
): Promise<AdultModelApplicationRecord> {
  const result = await requestAdultModelApplicationsApi<{ application: AdultModelApplicationRecord }>(
    `/events/model-applications/${encodeURIComponent(applicationId)}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        status: input.status,
        review_notes: input.reviewNotes?.trim() || '',
      }),
    },
  );

  return result.application;
}

export async function emailModelApplicationSummary(input: {
  recipientEmail: string;
  eventId?: string;
  status?: AdultModelApplicationStatus | ChildModelApplicationStatus | 'all';
  modelType?: 'all' | 'adult' | 'child';
}): Promise<{ sent: boolean; recipient_email: string; count: number }> {
  const result = await requestAdultModelApplicationsApi<{
    sent: boolean;
    recipient_email: string;
    count: number;
  }>('/events/model-applications/email-summary', {
    method: 'POST',
    body: JSON.stringify({
      recipient_email: input.recipientEmail,
      event_id: input.eventId ?? '',
      status: input.status ?? 'all',
      model_type: input.modelType ?? 'all',
    }),
  });

  return result;
}

export async function getModelApplicationNotificationRecipient(): Promise<ModelApplicationNotificationSettings> {
  return requestAdultModelApplicationsApi<ModelApplicationNotificationSettings>(
    '/events/model-applications/notification-recipient',
  );
}

export async function updateModelApplicationNotificationRecipient(input: {
  recipientEmail: string;
}): Promise<ModelApplicationNotificationSettings> {
  return requestAdultModelApplicationsApi<ModelApplicationNotificationSettings>(
    '/events/model-applications/notification-recipient',
    {
      method: 'PUT',
      body: JSON.stringify({
        recipient_email: input.recipientEmail.trim(),
      }),
    },
  );
}
